import { generateId, type Encounter } from '@gm-binder/shared';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Badge, Button, MarkdownView, EmptyState } from '../ui';
import { useBinder } from '../../lib/useBinder';
import { useRunStore } from '../../store/runStore';
import { useUiStore } from '../../store/uiStore';
import shared from './sections.module.css';
import styles from './EncountersSection.module.css';

/** Prepared encounter list with one-click initiative setup. */
export function EncountersSection() {
  const ctx = useBinder();
  const updateState = useRunStore((s) => s.updateState);
  const toast = useUiStore((s) => s.toast);
  if (!ctx) return null;
  const { adventure, instance, state, slug } = ctx;
  void state;
  void slug;

  const addToInitiative = (encounter: Encounter) => {
    updateState((st) => {
      for (const entry of encounter.monsters ?? []) {
        const monster = adventure.monsters.find((m) => m.id === entry.id);
        const monsterName = monster?.name ?? entry.id;
        const count = Math.max(0, entry.count);
        for (let i = 0; i < count; i += 1) {
          st.initiative.push({
            id: generateId('init'),
            name: `${monsterName}${count > 1 ? ` ${i + 1}` : ''}`,
            kind: 'monster',
            init: null,
            hp: Number(monster?.hp) || null,
            maxHp: Number(monster?.hp) || null,
            ac: monster?.ac ?? null,
            conditions: [],
            ref: monster?.id ?? entry.id,
          });
        }
      }
    });
    toast('Aggiunti all\'iniziativa', 'success');
  };

  return (
    <div>
      <PageHeader eyebrow="Incontri" title="Incontri preparati" />

      {!adventure.encounters.length ? (
        <EmptyState
          icon="⚔️"
          title="Nessun incontro"
          description="Questa avventura non contiene incontri preparati."
        />
      ) : (
        <div className={shared.stack}>
          {adventure.encounters.map((encounter, index) => {
            const room = encounter.room
              ? adventure.rooms.rooms.find((r) => r.id === encounter.room)
              : undefined;
            const monsters = encounter.monsters ?? [];
            return (
              <Card key={encounter.id ?? encounter.name ?? encounter.room ?? `encounter-${index}`}>
                <div className={styles.cardTop}>
                  <div className={shared.entityHead}>
                    <span className={shared.entityIcon} aria-hidden>
                      ⚔️
                    </span>
                    <h2 className={shared.entityName}>{encounter.name ?? 'Incontro'}</h2>
                  </div>
                  <div className={shared.cardActions}>
                    <Button
                      size="sm"
                      icon="⏱️"
                      onClick={() => addToInitiative(encounter)}
                      disabled={!monsters.length}
                    >
                      Aggiungi all'iniziativa
                    </Button>
                  </div>
                </div>

                <div className={shared.tagRow}>
                  {encounter.difficulty && <Badge tone="warn">{encounter.difficulty}</Badge>}
                  {encounter.room &&
                    (room ? (
                      <Link
                        to={`/run/${instance.id}/rooms/${room.id}`}
                        className={shared.chip}
                      >
                        📍 {room.name}
                      </Link>
                    ) : (
                      <span className={shared.chip}>📍 {encounter.room}</span>
                    ))}
                </div>

                {!!monsters.length && (
                  <div className={shared.sectionBlock}>
                    <div className={shared.blockLabel}>Mostri</div>
                    <ul className={styles.monsterList}>
                      {monsters.map((entry, monsterIndex) => {
                        const monster = adventure.monsters.find((m) => m.id === entry.id);
                        return (
                          <li key={`${entry.id}-${monsterIndex}`}>
                            {entry.count}×{' '}
                            {monster ? (
                              <Link to={`/run/${instance.id}/bestiary?focus=${monster.id}`}>
                                {monster.name}
                              </Link>
                            ) : (
                              (entry.id)
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {encounter.notes && <MarkdownView>{encounter.notes}</MarkdownView>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
